import type { ComponentProps } from "react";
import { Link } from "@tanstack/react-router";
import type { Row } from "@tanstack/react-table";
import { flexRender } from "@tanstack/react-table";
import { tv } from "tailwind-variants";

import { TableCell } from "./table-cell";
import { TableRow } from "./table-row";

export type RowLinkProps = {
  to: string;
  params?: Record<string, string | number>;
  search?: Record<string, unknown>;
};

type TableContentProps<TData> = {
  rows: Row<TData>[];
  rowLink?: (row: TData) => RowLinkProps;
  rowLinkLabel?: (row: TData) => string;
};

const tableContentVariants = tv({
  slots: {
    cell: "truncate",
    row: "",
    /*
      A real anchor, stretched over its whole row: the row keeps one target and
      one hover, and the destination gains an href, so it carries an accessible
      name, takes keyboard focus, and opens in a new tab on cmd or middle click.
    */
    rowLink: [
      "after:absolute after:inset-0 after:content-['']",
      "focus:outline-none focus-visible:after:ring-2 focus-visible:after:ring-focus-ring focus-visible:after:ring-inset",
    ],
  },
  variants: {
    actions: {
      true: { cell: "py-0" },
    },
    linked: {
      true: {
        row: [
          "relative cursor-pointer hover:bg-surface-hover",
          "data-[state=selected]:bg-accent-subtle",
          /* Controls in the other cells have to stay above the covering link. */
          "[&_button]:relative [&_button]:z-[1]",
          "[&_a:not([data-row-link])]:relative [&_a:not([data-row-link])]:z-[1]",
        ],
      },
    },
  },
});

const { cell: cellClass, row: rowClass, rowLink: rowLinkClass } = tableContentVariants();

export const TableContent = <TData,>({ rowLink, rowLinkLabel, rows }: TableContentProps<TData>) => {
  return (
    <>
      {rows.map((row) => {
        return (
          <TableRow
            className={rowClass({ linked: Boolean(rowLink) })}
            data-state={row.getIsSelected() ? "selected" : undefined}
            key={row.id}
          >
            {row.getVisibleCells().map((cell, index) => {
              const content = flexRender(cell.column.columnDef.cell, cell.getContext());

              return (
                <TableCell
                  className={cellClass({ actions: cell.column.id === "actions" })}
                  key={cell.id}
                >
                  {rowLink && index === 0 ? (
                    <>
                      {rowLinkLabel ? content : null}

                      {/*
                        Link's `to` and `params` are correlated through router
                        generics that cannot survive being passed down through the
                        table primitives, so the destination is typed structurally
                        here and asserted once, rather than typing every table as a
                        router-aware component.
                      */}
                      <Link
                        className={rowLinkClass()}
                        data-row-link=""
                        {...(rowLink(row.original) as ComponentProps<typeof Link>)}
                      >
                        {rowLinkLabel ? (
                          <span className="sr-only">{rowLinkLabel(row.original)}</span>
                        ) : (
                          content
                        )}
                      </Link>
                    </>
                  ) : (
                    content
                  )}
                </TableCell>
              );
            })}
          </TableRow>
        );
      })}
    </>
  );
};
